import React from 'react'
import PropTypes from 'prop-types'

// DS 3.0 shield-with-padlock (vuesax outline + bold, 24px) — IC-51144. Not in
// iconsax-react, so this mirrors that package's Icon shape (variant/color/size props, ref
// forwarding) so it drops straight into NavSection's ICONSAX_ICON_MAP next to the real
// iconsax icons. Email Security and Email Encryption both use it (IC-52560). Source
// paths sit in a 19.33x21.49 box centered in the 24x24 frame.
//
// The Outline padlock `d` is already an expanded outline (outer silhouette plus the
// body and shackle holes). A stroke on that ribbon paints a second edge and reads as
// a solid lock, so the padlock is filled evenodd with no stroke (IC-52560).
const Outline = ({ color }) => (
  <g transform="translate(2.335 1.255)">
    <path
      d="M18.57 9.8625C18.57 14.7525 15.02 19.3325 10.17 20.6725C9.84001 20.7625 9.48001 20.7625 9.15001 20.6725C4.3 19.3325 0.75 14.7525 0.75 9.8625V5.4725C0.75 4.6525 1.37 3.7225 2.14 3.4125L7.71001 1.1325C8.96001 0.6225 10.37 0.6225 11.62 1.1325L17.19 3.4125C17.95 3.7225 18.58 4.6525 18.58 5.4725L18.57 9.8625Z"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.42004 4.74243C11 4.74248 12.2902 6.03258 12.2902 7.61255V8.02368C13.3828 8.38745 14.17 9.41641 14.17 10.6321V12.4817C14.17 14.0017 12.94 15.2317 11.42 15.2317H7.42004C5.90004 15.2317 4.67004 14.0017 4.67004 12.4817V10.6321C4.67004 9.41641 5.45725 8.38745 6.54993 8.02368V7.61255C6.54993 6.03255 7.84004 4.74243 9.42004 4.74243ZM7.42004 9.38208C6.73004 9.38208 6.17004 9.94208 6.17004 10.6321V12.4817C6.17004 13.1717 6.73004 13.7317 7.42004 13.7317H11.42C12.11 13.7317 12.67 13.1717 12.67 12.4817V10.6321C12.67 9.94208 12.11 9.38208 11.42 9.38208H7.42004ZM9.42004 6.24243C8.66004 6.24243 8.04993 6.85255 8.04993 7.61255V7.88208H10.7902V7.61255C10.7902 6.85258 10.18 6.24248 9.42004 6.24243Z"
      fill={color}
      fillRule="evenodd"
    />
  </g>
)

Outline.propTypes = { color: PropTypes.string.isRequired }

const Bold = ({ color }) => (
  <g transform="translate(2.335 1.255)">
    <path
      d="M7.42676 0.438075C8.85837 -0.146025 10.4717 -0.146025 11.9033 0.438075L17.4727 2.71737C18.0186 2.9401 18.4748 3.36269 18.791 3.83065C19.1071 4.29865 19.3301 4.88045 19.3301 5.47225V5.47421L19.3203 9.86386C19.3193 15.1023 15.5327 19.9686 10.3701 21.3951L10.3672 21.3961C9.90827 21.5212 9.41205 21.5211 8.95312 21.3961L8.9502 21.3951C3.78691 19.9685 0.000122141 15.1011 0 9.8619V5.47225C0 4.88314 0.21841 4.3024 0.53418 3.83261C0.849265 3.36391 1.3046 2.94145 1.85547 2.71835L7.42676 0.438075ZM9.41992 4.74179C7.83998 4.74185 6.5498 6.03194 6.5498 7.6119V8.02304C5.4572 8.38685 4.66992 9.41582 4.66992 10.6314V12.481C4.66997 14.001 5.90001 15.231 7.41992 15.231H11.4199C12.9399 15.231 14.1699 14.001 14.1699 12.481V10.6314C14.1699 9.41577 13.3827 8.38681 12.29 8.02304V7.6119C12.29 6.03194 10.9999 4.74184 9.41992 4.74179ZM9.41992 6.24179C10.1799 6.24184 10.79 6.85194 10.79 7.6119V7.88143H8.0498V7.6119C8.0498 6.85194 8.65998 6.24185 9.41992 6.24179Z"
      fill={color}
      fillRule="evenodd"
      clipRule="evenodd"
    />
  </g>
)

Bold.propTypes = { color: PropTypes.string.isRequired }

const chooseVariant = (variant, color) =>
  variant === 'Bold' ? <Bold color={color} /> : <Outline color={color} />

/**
 * EmailEncryptionIcon — DS 3.0 shield-with-padlock. Mapped to Email Security and
 * Email Encryption. Mirrors iconsax's `Icon` prop contract so it's a drop-in for
 * `ICONSAX_ICON_MAP` — only `Outline`/`Bold` variants exist (Figma delivered only those).
 */
const EmailEncryptionIcon = React.forwardRef(({ variant, color, size, ...rest }, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <svg
    {...rest}
    ref={ref}
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
  >
    {chooseVariant(variant, color)}
  </svg>
))

EmailEncryptionIcon.propTypes = {
  variant: PropTypes.oneOf(['Outline', 'Bold']),
  color: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

EmailEncryptionIcon.defaultProps = {
  variant: 'Outline',
  color: 'currentColor',
  size: '24',
}

EmailEncryptionIcon.displayName = 'EmailEncryptionIcon'

export default EmailEncryptionIcon
